import { queryOptions } from '@tanstack/react-query';
import type { ApiClient, AlertsListResponse, AlertRulesListResponse } from '@/lib/api';
import { ApiContractError } from '@/lib/fetch-utils';

export function alertEventsQueryKey(selectedOrgId: string | null) {
  return ['alerts', 'events', selectedOrgId] as const;
}

export function alertEventsOptions(apiClient: ApiClient, selectedOrgId: string | null) {
  return queryOptions({
    queryKey: alertEventsQueryKey(selectedOrgId),
    enabled: !!selectedOrgId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: (): Promise<AlertsListResponse> => apiClient.alerts.getAlerts(),
  });
}

export function alertsActiveCountQueryKey(selectedOrgId: string | null) {
  return ['alerts', 'active-count', selectedOrgId] as const;
}

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical';

/**
 * The active alerts, counted: `total` — what the caller reads; `resolvable`
 * — what «Resolve all» would close. The two differ only for a managed subject
 * (an access grant), whose list is read by its grant while the sweep is role
 * ∧ grant; for everyone else the server sends no separate count and they are
 * the same number.
 */
type ActiveAlertCounts = { total: number; resolvable: number };

/**
 * What narrows an active count. Absent — the caller's whole list, the one the
 * sidebar badge and the alerts screen read. The route takes one project and no
 * window, and its demo policy defaults to «yes».
 */
export interface ActiveAlertCountScope {
  severity?: AlertSeverity;
  projectId?: string;
  includeDemo?: 'yes' | 'no';
}

function activeAlertCountsOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  scope: ActiveAlertCountScope = {}
) {
  const { severity, projectId, includeDemo } = scope;
  const scoped =
    severity !== undefined || projectId !== undefined || includeDemo !== undefined;
  return {
    // A scoped entry extends the unfiltered key, so invalidating the org's
    // active count refreshes every scope too: the sidebar badge, the alerts
    // screen's severities and the Summary's card.
    queryKey: scoped
      ? ([
          ...alertsActiveCountQueryKey(selectedOrgId),
          { severity, projectId, includeDemo },
        ] as const)
      : alertsActiveCountQueryKey(selectedOrgId),
    enabled: !!selectedOrgId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<ActiveAlertCounts> => {
      const res = await apiClient.alerts.getAlerts({
        status: 'active',
        limit: 1,
        severity,
        projectId,
        includeDemo,
      });
      // At `limit=1` the rows are no count: an answer without `total` is a
      // failure, never a 0 and never the one row's length. The route always
      // sends it.
      if (typeof res.total !== 'number') {
        throw new ApiContractError('The alerts answer has no total');
      }
      return { total: res.total, resolvable: res.resolvableTotal ?? res.total };
    },
  };
}

/**
 * Sidebar badge source: minimal payload (limit=1) just to read `total` from
 * the server's count query, scoped to `status=active`. Avoids the badge
 * being capped by the events page-size of the main list query.
 *
 * With a scope (the Summary's «Открытые сигналы»: one project, a demo policy)
 * it is the same request narrowed, under a key inside the same family.
 */
export function alertsActiveCountOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  scope?: Pick<ActiveAlertCountScope, 'projectId' | 'includeDemo'>
) {
  return queryOptions({
    ...activeAlertCountsOptions(apiClient, selectedOrgId, scope),
    select: (counts: ActiveAlertCounts) => counts.total,
  });
}

/**
 * «Resolve all (N)»: how many active alerts the bulk resolve would actually
 * close. Without a severity — the same request and cache entry as the badge,
 * another view of it; with one — the server's count of that severity, which
 * the loaded page cannot give (it is a slice of the list).
 */
export function alertsResolvableCountOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  severity?: AlertSeverity
) {
  return queryOptions({
    ...activeAlertCountsOptions(apiClient, selectedOrgId, { severity }),
    select: (counts: ActiveAlertCounts) => counts.resolvable,
  });
}

export function alertRulesQueryKey(selectedOrgId: string | null) {
  return ['alerts', 'rules', selectedOrgId] as const;
}

export function alertRulesOptions(apiClient: ApiClient, selectedOrgId: string | null) {
  return queryOptions({
    queryKey: alertRulesQueryKey(selectedOrgId),
    enabled: !!selectedOrgId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: (): Promise<AlertRulesListResponse> => apiClient.alerts.getAlertRules(),
  });
}
